import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "dd-cookie-consent";

/**
 * SECURITY NOTE — Why localStorage is the correct choice here:
 *
 * This module stores ONLY a non-sensitive UI consent flag with value
 * 'all' | 'essential' (a boolean choice, not user data). It contains:
 *   - NO personal data, NO authentication tokens, NO PII
 *   - NO identifiers that could be used for tracking
 *
 * Cookie/consent banners industry-wide use localStorage because:
 *   1. The consent choice MUST persist across browser sessions
 *      (sessionStorage would re-prompt on every visit — defeating the purpose
 *      and contravening the user's stated preference)
 *   2. httpOnly cookies require server-side rendering of the choice; this is
 *      a static site, so there is no server session to attach a cookie to
 *   3. The value is harmless if exposed via XSS — an attacker reading
 *      'essential' learns nothing useful
 *
 * If a future change introduces ACTUAL personal data here, swap to a backend
 * consent endpoint + httpOnly cookie.
 */
export const getConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.choice || null;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[CookieBanner] Could not read consent:", err);
    }
    return null;
  }
};

const persistConsent = (choice) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, at: new Date().toISOString() })
    );
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[CookieBanner] Could not persist consent (private mode?):", err);
    }
  }
};

const BannerHeader = () => (
  <div className="w-10 h-10 rounded-xl bg-daisy grid place-items-center shrink-0">
    <Cookie className="w-5 h-5 text-forest" />
  </div>
);

const BannerCopy = () => (
  <p className="mt-1 text-base text-charcoal/85">
    We use only essential cookies to make this site work. We'd also like to use a small amount of basic analytics to
    understand which pages help most. See our{" "}
    <Link to="/cookies" className="underline text-forest font-semibold" data-testid="cookie-banner-policy-link">
      Cookie Policy
    </Link>
    .
  </p>
);

const BannerActions = ({ onAcceptAll, onEssential }) => (
  <div className="mt-4 flex flex-col sm:flex-row gap-2">
    <button type="button" onClick={onAcceptAll} className="btn-primary h-11 px-5 text-base" data-testid="cookie-accept-all">
      Accept all
    </button>
    <button type="button" onClick={onEssential} className="btn-ghost h-11 px-5 text-base" data-testid="cookie-accept-essential">
      Essential only
    </button>
  </div>
);

const CookieBanner = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!getConsent()) setOpen(true);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const save = useCallback((choice) => {
    persistConsent(choice);
    setOpen(false);
    window.dispatchEvent(new CustomEvent("dd:cookie-consent", { detail: { choice } }));
  }, []);

  if (!open) return null;

  return (
    <div
      role="region"
      aria-label="Cookie preferences"
      className="fixed left-4 right-4 lg:right-auto lg:left-6 z-50 bottom-24 lg:bottom-6 max-w-md soft-card p-5 sm:p-6 bg-cream border-2 border-forest/15 shadow-2xl reveal"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <BannerHeader />
        <div className="flex-1">
          <h3 className="font-serif text-xl text-forest">A quick note on cookies</h3>
          <BannerCopy />
          <BannerActions onAcceptAll={() => save("all")} onEssential={() => save("essential")} />
        </div>
        <button
          aria-label="Dismiss cookie banner"
          type="button"
          onClick={() => save("essential")}
          className="text-forest/70 hover:text-forest p-1 -mt-1 -mr-1"
          data-testid="cookie-dismiss"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
